import { type WalletConnectionErrorType, type WalletConnectionStatus } from '@/app/api/t/constants'
import type { WalletType } from '@/lib/observability/shared/wallet-type'

import { createDurationTimer, sendMetricBeacon } from '../../utils/metric-beacon'

import { classifyWalletConnectionError, isWalletConnectionUserRejection } from './error-classifier'

/** Wire contract for `/api/t/wallet-connection`; field names mirror the transaction beacon. */
interface WalletConnectionMetricPayload {
  provider: string
  outcome: WalletConnectionStatus
  durationSeconds?: number
  errorCategory?: WalletConnectionErrorType
  errorMessage?: string
  browser?: string
  platform?: 'desktop' | 'mobile'
  chainId?: number
  walletType?: WalletType
}

interface TrackWalletConnectionParams {
  provider: string
  status: WalletConnectionStatus
  durationSeconds?: number
  error?: unknown
  chainId?: number
  walletType?: WalletType
}

interface WalletConnectionTrackerParams {
  provider: string
  chainId?: number
  walletType?: WalletType
}

function detectBrowser(): string {
  if (typeof navigator === 'undefined') return 'unknown'

  const ua = navigator.userAgent.toLowerCase()

  if (ua.includes('brave')) return 'brave'
  if (ua.includes('edg')) return 'edge'
  if (ua.includes('chrome')) return 'chrome'
  if (ua.includes('firefox')) return 'firefox'
  if (ua.includes('safari')) return 'safari'
  if (ua.includes('opera') || ua.includes('opr')) return 'opera'

  return 'other'
}

function detectPlatform(): 'desktop' | 'mobile' {
  if (typeof navigator === 'undefined') return 'desktop'

  const ua = navigator.userAgent.toLowerCase()
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)

  return isMobile ? 'mobile' : 'desktop'
}

const KNOWN_PROVIDERS = [
  'metamask',
  'walletconnect',
  'coinbase',
  'rabby',
  'safe',
  'rainbow',
  'trust',
  'zerion',
  'okx',
  'phantom',
  'ledger',
  'trezor',
  'frame',
  'argent',
  'gnosis'
] as const

/** Vendor flags injected wallets set on `window.ethereum`, in detection order. */
const INJECTED_PROVIDER_FLAGS: ReadonlyArray<readonly [flag: string, provider: string]> = [
  ['isMetaMask', 'metamask'],
  ['isRabby', 'rabby'],
  ['isCoinbaseWallet', 'coinbase'],
  ['isBraveWallet', 'brave'],
  ['isPhantom', 'phantom'],
  ['isOKExWallet', 'okx'],
  ['isOkxWallet', 'okx'],
  ['isTrust', 'trust'],
  ['isZerion', 'zerion']
]

function detectInjectedProvider(): string | undefined {
  const ethereum = typeof window === 'undefined' ? undefined : window.ethereum
  if (!ethereum) return undefined

  for (const [flag, provider] of INJECTED_PROVIDER_FLAGS) {
    if (ethereum[flag]) return provider
  }

  return undefined
}

export function normalizeProviderName(connectorId?: string, connectorName?: string): string {
  const id = (connectorId || '').toLowerCase()
  const name = (connectorName || '').toLowerCase()

  for (const provider of KNOWN_PROVIDERS) {
    if (id.includes(provider) || name.includes(provider)) {
      return provider
    }
  }

  if (id === 'injected' || name.includes('injected')) {
    return detectInjectedProvider() ?? 'injected'
  }

  return name || id || 'unknown'
}

export function trackWalletConnection(params: TrackWalletConnectionParams): void {
  const { provider, status, durationSeconds, error, chainId, walletType } = params

  let actualStatus: WalletConnectionStatus = status
  if (status === 'failure' && error && isWalletConnectionUserRejection(error)) {
    actualStatus = 'user_rejected'
  }

  const payload: WalletConnectionMetricPayload = {
    provider: normalizeProviderName(provider),
    outcome: actualStatus,
    durationSeconds,
    errorCategory: actualStatus === 'failure' ? classifyWalletConnectionError(error) : undefined,
    errorMessage:
      actualStatus === 'failure' && error instanceof Error
        ? error.message.slice(0, 200)
        : undefined,
    browser: detectBrowser(),
    platform: detectPlatform(),
    chainId,
    walletType
  }

  sendMetricBeacon('/api/t/wallet-connection', payload)
}

export function createWalletConnectionTracker(params: WalletConnectionTrackerParams) {
  const timer = createDurationTimer()

  return {
    success: (chainId?: number) => {
      trackWalletConnection({
        ...params,
        chainId: chainId ?? params.chainId,
        status: 'success',
        durationSeconds: timer.elapsed()
      })
    },

    failure: (error?: unknown) => {
      trackWalletConnection({
        ...params,
        status: 'failure',
        durationSeconds: timer.elapsed(),
        error
      })
    }
  }
}
